import React, { useState, useEffect, useMemo } from "react";
import { Form, Input, Button, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useScanQrcodeStore } from "@/store";
import { VALIDATION_RULES } from "@/constants";
import { isEmpty } from "@/utils";
import { FormVerificationData } from "@/types";

interface FormVerificationProps {
  preview?: boolean;
  loading?: boolean;
  className?: string;
  shouldShowCustomerFields?: boolean;
  onSubmit: (data: FormVerificationData) => void;
}

interface FormData {
  verification_code: string;
  name: string;
  phone: string;
}

const FormVerification: React.FC<FormVerificationProps> = ({
  preview = false,
  loading = false,
  className = "",
  shouldShowCustomerFields = false,
  onSubmit,
}) => {
  const { t }: any = useTranslation();
  const [form] = Form.useForm();
  const [isProcessing, setIsProcessing] = useState(false);
  const { stamp_code, customer, getKeyThemeData } = useScanQrcodeStore();

  const verificationCode = stamp_code?.verification_code || "";
  const colorSuccess = getKeyThemeData("color_success") || "#00994D";

  const isDisableVerifyCode = useMemo(() => {
    return !isEmpty(verificationCode);
  }, [verificationCode]);

  useEffect(() => {
    const initialValues: FormData = {
      verification_code: verificationCode,
      name: customer?.name || "",
      phone: customer?.phone || "",
    };

    form.setFieldsValue(initialValues);
  }, [form, verificationCode, customer]);

  const handleSubmit = async (values: FormData) => {
    if (preview) {
      console.log("⚠️ Preview mode - submission blocked");
      return;
    }

    setIsProcessing(true);

    try {
      await onSubmit(values);
    } catch (error) {
      console.error("❌ Form submission error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitFailed = (errorInfo: any) => {
    setIsProcessing(false);
  };

  // Validation rules
  const getValidationRules = () => {
    const rules: any = {
      verification_code: [{ required: true, message: t("form.verification_code_required") }],
    };

    if (shouldShowCustomerFields) {
      rules.name = [
        { required: true, message: t("form.name_required") },
        {
          min: VALIDATION_RULES.NAME_MIN_LENGTH,
          message: t("form.name_min_length", { min: VALIDATION_RULES.NAME_MIN_LENGTH }),
        },
        {
          max: VALIDATION_RULES.NAME_MAX_LENGTH,
          message: t("form.name_max_length", { max: VALIDATION_RULES.NAME_MAX_LENGTH }),
        },
      ];

      rules.phone = [
        { required: true, message: t("form.phone_required") },
        {
          pattern: VALIDATION_RULES.PHONE_PATTERN,
          message: t("form.phone_invalid"),
        },
      ];
    }

    return rules;
  };

  return (
    <div className={className}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={handleSubmitFailed}
        requiredMark={false}
        size="large"
      >
        <Form.Item
          name="verification_code"
          label={t("common.verify_code")}
          rules={getValidationRules().verification_code}
        >
          <Input placeholder={t("placeholders.verify_code")} disabled={isDisableVerifyCode} size="large" />
        </Form.Item>

        {shouldShowCustomerFields && (
          <Form.Item name="name" label={t("common.customer_name")} rules={getValidationRules().name}>
            <Input placeholder={t("placeholders.customer_name")} size="large" />
          </Form.Item>
        )}

        {shouldShowCustomerFields && (
          <Form.Item name="phone" label={t("common.phone_number")} rules={getValidationRules().phone}>
            <Input placeholder={t("placeholders.phone_number")} size="large" />
          </Form.Item>
        )}

        <Form.Item>
          <Tooltip title={preview ? t("warning.preview_mode") : ""} placement="top">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading || isProcessing}
              disabled={!!preview || loading || isProcessing}
              className="w-full !mt-2 min-h-[48px]"
              size="large"
              style={{
                backgroundColor: preview ? undefined : colorSuccess,
                borderColor: preview ? undefined : colorSuccess,
              }}
            >
              {t("buttons.verification")}
            </Button>
          </Tooltip>
        </Form.Item>
      </Form>
    </div>
  );
};

export default FormVerification;
